'use client';

import { EPlatform } from '@/utils/enums/platform-enum';
import React, { useCallback, useEffect } from 'react';
import { useTranslations } from '@/i18n/use-translations';

import { useHomeSensorsTracking } from './hooks/useHomeSensorsTracking';
import { useHomeThirdPartyTracking } from './hooks/useHomeThirdPartyTracking';
import HomeSectionRenderer, {
  type HomeComponentMap,
  type HomeServerRenderPolicy,
} from './HomeSectionRenderer';

interface HomePageBlockItem {
  id?: number;
  component_id?: string;
  jump_target?: any[];
  [key: string]: any;
}

interface StreamingHomePageProps {
  homeListData: HomePageBlockItem[];
  deviceType?: EPlatform;
  componentMap?: HomeComponentMap;
  serverRenderPolicy?: HomeServerRenderPolicy;
}

export default function StreamingHomePage({
  homeListData,
  deviceType = EPlatform.WEB,
  componentMap,
  serverRenderPolicy = 'all',
}: StreamingHomePageProps) {
  const t = useTranslations('homePage');

  const { trackHomeView, trackHomeMktClick, trackHomeMktValidExp, trackHomeBtnClick } =
    useHomeSensorsTracking();

  const { initHomeThirdPartyTracking } = useHomeThirdPartyTracking();

  const handleSDHomeMktClick = useCallback(
    (eventParams: any) => {
      trackHomeMktClick(eventParams);
    },
    [trackHomeMktClick],
  );

  const handleSDHomeMktValidExp = useCallback(
    (eventParams: any) => {
      trackHomeMktValidExp(eventParams);
    },
    [trackHomeMktValidExp],
  );

  useEffect(() => {
    const scheduleTracking = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            trackHomeView();
          },
          { timeout: 500 },
        );
      } else {
        setTimeout(trackHomeView, 100);
      }
    };

    scheduleTracking();

    const thirdPartyTimer = setTimeout(() => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(
          () => {
            initHomeThirdPartyTracking();
          },
          { timeout: 1000 },
        );
      } else {
        initHomeThirdPartyTracking();
      }
    }, 1000);

    return () => {
      clearTimeout(thirdPartyTimer);
    };
  }, [trackHomeView, initHomeThirdPartyTracking]);

  // 数据验证：如果没有数据则显示空状态
  if (!homeListData || homeListData.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-500">{t('noContent')}</div>
        </div>
      </div>
    );
  }

  return (
    <div id="main-box" className="homepage w-full">
      <HomeSectionRenderer
        listData={homeListData}
        deviceType={deviceType}
        onTrack={handleSDHomeMktClick}
        onValidExpTrack={handleSDHomeMktValidExp}
        onBtnTrack={trackHomeBtnClick}
        homeListData={homeListData}
        componentMap={componentMap}
        serverRenderPolicy={serverRenderPolicy}
      />
    </div>
  );
}

export function StreamingProvider({ children }: { children: React.ReactNode }) {
  return <div className="homepage-provider">{children}</div>;
}
